<template>
  <div class="optionsInputs">
    <div class="optionsInputs__title">
      <p class="title">
        {{ $t('optionsUpperCase') }}
      </p>
    </div>
    <FormKit type="group" name="options">
      <div
        v-for="(option, index) in options"
        :key="index"
        class="optionsInputs-container"
      >
        <FormKit type="group" :name="index">
          <div class="options__dropdown-block">
            <Checkbox
              :name="'enabled_' + index"
              :value="option.enabled"
              :title="$t('active')"
              :checked="option.enabled"
              @change="option.enabled = !option.enabled"
            />
            <p
              :class="{
                'title-options': true,
                'title-options-error': positionConflict(index) && option.enabled
              }"
            >
              {{ $t('position') }}
            </p>
            <Dropdown
              :class="{
                'dropdown-positions': true,
                'dropdown-disabled': !option.enabled
              }"
              :selected-option="selectedPositions.selectedOption[index]"
              :options="selectedPositions.options"
              :disabled="!option.enabled"
              @select-option="(option) => selectPositions(index, option)"
            />
            <span
              v-if="positionConflict(index) && option.enabled"
              class="error-message"
            >
              {{ $t('theSamePositions') }}
            </span>
          </div>
          <div class="options__infoTemplates">
            <p
              :class="{
                'options__infoTemplates-name': true,
                'option-disabled': !option.enabled
              }"
            >
              {{ index }}
            </p>
            <div class="options__infoTemplates__translations">
              <FormKit type="group" name="translations">
                <div
                  v-for="translation in translations"
                  :key="translation.value"
                  class="container"
                >
                  <span>{{ translation.label }}</span>
                  <Input
                    :class="{ 'input-disabled': !option.enabled }"
                    type="text"
                    :name="translation.value"
                    :value="option.translations[translation.value]"
                    :disabled="disabledInput(option)"
                  />
                </div>
              </FormKit>
            </div>
          </div>
        </FormKit>
      </div>
    </FormKit>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';

import { translations } from '../../configs';
import { mapOptionsToSelectedPositions, optionsPositions } from '../configs';

import { OptionsData } from '@/store/modules/pharmacy/types';

@Options({
  name: 'OptionsInputs',
  props: ['options', 'user'],
  emits: ['selectOptions'],
  data() {
    return {
      language: '',
      translations,
      selectedPositions: {
        selectedOption: mapOptionsToSelectedPositions(
          this.options,
          optionsPositions
        ),
        options: optionsPositions
      }
    };
  },
  created() {
    if (this.user) {
      this.language = this.user?.appLanguage;
    }
  },
  computed: {
    userAppLanguage() {
      return this.user?.appLanguage;
    }
  },
  methods: {
    disabledInput(option: OptionsData) {
      if (!option.enabled) {
        return true;
      } else {
        return false;
      }
    },
    selectPositions(key: string, option: OptionsData) {
      this.selectedPositions.selectedOption[key] = option;
      this.$emit('selectOptions', { key, option });
    },
    positionConflict(key: string) {
      const selectedPosition = this.selectedPositions.selectedOption[key];
      const enabledOptions = Object.keys(
        this.selectedPositions.selectedOption
      ).filter((otherKey) => this.options[otherKey].enabled);

      return enabledOptions.some((otherKey) => {
        if (otherKey !== key) {
          const option = this.selectedPositions.selectedOption[otherKey];

          return option.value === selectedPosition.value;
        }

        return false;
      });
    }
  },
  watch: {
    options: {
      immediate: true,
      handler(newOptions) {
        this.selectedPositions.selectedOption = mapOptionsToSelectedPositions(
          newOptions,
          optionsPositions
        );
      }
    }
  }
})
export default class OptionsInputs extends Vue {}
</script>

<style lang="scss">
.optionsInputs {
  .optionsInputs__title {
    position: relative;
    margin: 32px 0;
    .title {
      font-family: 'Ubuntu-Regular';
      font-size: $sixteenSize;
      color: $servicesTitle;
    }
    &::after {
      content: '';
      position: absolute;
      top: 9px;
      left: 125px;
      width: 150px;
      height: 1px;
      background: $ligher-grey;
    }
    &::before {
      content: '';
      position: absolute;
      top: 7px;
      left: 120px;
      width: 5px;
      height: 5px;
      border-radius: 8px;
      background: $ligher-grey;
    }
  }
  .optionsInputs-container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: flex-start;
    .options__dropdown-block {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
      .dropdown-positions {
        width: 104px;
        height: 30px;
        &.dropdown-disabled {
          .dropdown__head {
            color: $servicesTitle;
            background-color: $timePicker;
            .dropdown__head-content {
              background-color: $timePicker;
              .dropdown__head-content-input {
                background-color: $timePicker;
              }
            }
            .dropdown__head-img {
              background-color: $timePicker;
            }
          }
        }
      }
      .title-options {
        font-size: 16px;
        margin-left: 100px;
        margin-right: 15px;
        &.title-options-error {
          color: $red;
        }
      }
      .error-message {
        color: $red;
        margin-left: 15px;
      }
    }
    .options__infoTemplates {
      display: flex;
      .options__infoTemplates-name {
        width: 335px;
        height: 30px;
        font-size: 14px;
        border-radius: 8px;
        border: 1px solid #e5e5e5;
        padding-left: 15px;
        padding-top: 16px;
        &.option-disabled {
          color: $servicesTitle;
          background-color: $timePicker;
        }
      }
      .options__infoTemplates__translations {
        margin-left: 100px;
        margin-bottom: 40px;
        width: 450px;
        .container {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 30px;
          &:last-child {
            margin-bottom: 0;
          }
          .input {
            display: inline-block;
            width: 350px;
            margin-left: 15px;
            margin-bottom: 0;
            &.input-disabled {
              color: $servicesTitle;
              background-color: $timePicker;
              .formkit-outer {
                .formkit-wrapper {
                  .formkit-input {
                    color: $servicesTitle;
                    background-color: $timePicker;
                  }
                }
              }
            }
            .formkit-input {
              height: 48px;
            }
          }
        }
      }
    }
  }
}
</style>
